import L from "../../libs/leaflet";
import React, { cloneElement } from "react";
import { createRoot } from "react-dom/client";

/**
 * Construye uno o varios controles personalizados de Leaflet
 * a partir de una configuración de botones.
 *
 * Cada botón puede definir:
 * - icono React
 * - título
 * - posición
 * - colores
 * - identificador
 * - acción al hacer clic
 *
 * Retorna objetos de control que exponen:
 * - control: instancia de L.Control
 * - button: referencia al elemento <a>
 * - container: referencia al contenedor Leaflet
 * - show()
 * - hide()
 * - enable()
 * - disable()
 *
 * @param {Object|Object[]} buttonsConfig - Configuración de uno o varios botones.
 * @returns {Array<{
 *   control: L.Control,
 *   button: HTMLAnchorElement|null,
 *   container: HTMLDivElement|null,
 *   show: Function,
 *   hide: Function,
 *   enable: Function,
 *   disable: Function
 * }>}
 */
export function buildMapButtons(buttonsConfig) {
  const buildButton = (buttonConfig) => {
    let buttonRef = null;
    let containerRef = null;

    const Control = L.Control.extend({
      options: {
        position: buttonConfig.position ?? "topleft",
      },

      onAdd() {
        const container = L.DomUtil.create(
          "div",
          "leaflet-control leaflet-bar button-container",
        );

        const button = L.DomUtil.create(
          "a",
          "button-control",
          container,
        );

        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.gap = "6px";
        button.style.padding = "0 10px";
        button.style.minWidth = "36px";

        if (buttonConfig.color) {
          button.style.backgroundColor = buttonConfig.color;
        }

        if (buttonConfig.textColor) {
          button.style.color = buttonConfig.textColor;
        }
        button.href = "#";
        button.title = buttonConfig.name ?? "";
        button.id = buttonConfig.id ?? "";

        buttonRef = button;
        containerRef = container;

        const content = document.createElement("div");

        content.style.display = "flex";
        content.style.alignItems = "center";
        content.style.gap = "6px";

        button.appendChild(content);

        const root = createRoot(content);

        const iconEl = buttonConfig.icon
          ? cloneElement(buttonConfig.icon, {
            size: buttonConfig.iconSize ?? 20,
          })
          : null;

        root.render(
          <>
            {iconEl}
            {buttonConfig.title && (
              <span>{buttonConfig.title}</span>
            )}
          </>,
        );

        button.onclick = (e) => {
          e.preventDefault();
          buttonConfig.action?.(e);
        };

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.stopPropagation(container);

        return container;
      },
    });

    return {
      control: new Control(),

      get button() {
        return buttonRef;
      },

      get container() {
        return containerRef;
      },

      show() {
        if (containerRef) {
          containerRef.style.display = "";
        }
      },

      hide() {
        if (containerRef) {
          containerRef.style.display = "none";
        }
      },

      enable() {
        if (buttonRef) {
          buttonRef.style.pointerEvents = "";
          buttonRef.style.opacity = "";
        }
      },

      disable() {
        if (buttonRef) {
          buttonRef.style.pointerEvents = "none";
          buttonRef.style.opacity = "0.5";
        }
      },
    };
  };

  if (!buttonsConfig) return [];

  return Array.isArray(buttonsConfig)
    ? buttonsConfig.map(buildButton)
    : [buildButton(buttonsConfig)];
}